import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getPlaylist, getPlaylistTracks, deletePlaylist, materializePlaylist, updatePlaylist } from '../api';
import usePlayerStore from '../store/playerStore';
import NavControls from '../components/NavControls';
import useSortable from '../hooks/useSortable';
import TrackTable from '../components/TrackTable';

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ALL_COLUMNS = [
  { key: 'index',          label: '#',           default: true,  width: 40,  sortKey: null,          render: (t, i, cur) => cur?.id === t.id ? '♪' : i + 1 },
  { key: 'title',          label: 'Title',        default: true,  width: 220, sortKey: 'title',       render: t => t.title, bold: true },
  { key: 'artist_name',    label: 'Artist',       default: true,  width: 160, sortKey: 'artist_name', render: t => t.artist_name },
  { key: 'album_title',    label: 'Album',        default: true,  width: 160, sortKey: 'album_title', render: t => t.album_title },
  { key: 'track_number', label: 'Tr#', default: true, width: 50, sortKey: 'track_number', render: t => t.track_number || '—' },
  { key: 'genre_name',     label: 'Genre',        default: false, width: 120, sortKey: 'genre_name',  render: t => t.genre_name },
  { key: 'year',           label: 'Year',         default: false, width: 60,  sortKey: 'year',        render: t => t.year || '—' },
  { key: 'duration_display', label: 'Time',       default: true,  width: 60,  sortKey: 'duration',    render: t => t.duration_display },
  { key: 'play_count',     label: 'Plays',        default: false, width: 60,  sortKey: 'play_count',  render: t => t.play_count },
  { key: 'skip_count',     label: 'Skips',        default: false, width: 60,  sortKey: 'skip_count',  render: t => t.skip_count },
  { key: 'bpm',            label: 'BPM',          default: false, width: 60,  sortKey: 'bpm',         render: t => t.bpm || '—' },
  { key: 'bit_rate',       label: 'Bit Rate',     default: false, width: 80,  sortKey: 'bit_rate',    render: t => t.bit_rate ? `${t.bit_rate}k` : '—' },
  { key: 'file_format',    label: 'Format',       default: false, width: 70,  sortKey: 'file_format', render: t => t.file_format?.toUpperCase() || '—' },
  { key: 'is_loved',       label: 'Fav',          default: false, width: 40,  sortKey: 'is_loved',    render: t => t.is_loved ? '★' : '' },
  { key: 'date_added',     label: 'Date Added',   default: false, width: 100, sortKey: 'date_added',  render: t => t.date_added ? t.date_added.slice(0, 10) : '—' },
  { key: 'last_played',    label: 'Last Played',  default: false, width: 100, sortKey: 'last_played', render: t => t.last_played ? t.last_played.slice(0, 10) : '—' },
  { key: 'composer',       label: 'Composer',     default: false, width: 140, sortKey: 'composer',    render: t => t.composer || '—' },
];

const syncLabelStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', fontSize: 13,
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? 'var(--text-muted)' : 'var(--text)',
  opacity: disabled ? 0.5 : 1,
  userSelect: 'none',
});

const STORAGE_KEY = 'musicapp_playlist_columns';

function loadColumns() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
}

function saveColumns(keys) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(keys)); } catch {}
}

function ColumnPicker({ activeKeys, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (key) => {
    if (key === 'index' || key === 'title') return;
    const next = activeKeys.includes(key)
      ? activeKeys.filter(k => k !== key)
      : [...activeKeys, key];
    const ordered = ALL_COLUMNS.map(c => c.key).filter(k => next.includes(k));
    onChange(ordered);
    saveColumns(ordered);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-secondary"
        style={{ fontSize: 12, padding: '5px 12px' }}
        onClick={() => setOpen(o => !o)}
      >⚙ Columns</button>
      {open && (
        <div style={pickerStyles.dropdown}>
          <div style={pickerStyles.header}>Show / Hide Columns</div>
          {ALL_COLUMNS.map(col => {
            const locked = col.key === 'index' || col.key === 'title';
            const active = activeKeys.includes(col.key);
            return (
              <label key={col.key} style={pickerStyles.item(locked)}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={locked}
                  onChange={() => toggle(col.key)}
                  style={{ marginRight: 8 }}
                />
                {col.label}
                {locked && <span style={pickerStyles.locked}>always on</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const pickerStyles = {
  dropdown: {
    position: 'absolute', right: 0, top: '110%', zIndex: 100,
    background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '8px 0', minWidth: 200,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  header: {
    padding: '6px 16px 8px', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.1em',
    color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
    marginBottom: 4,
  },
  item: (locked) => ({
    display: 'flex', alignItems: 'center', padding: '6px 16px',
    fontSize: 13, cursor: locked ? 'default' : 'pointer',
    color: locked ? 'var(--text-muted)' : 'var(--text)',
    opacity: locked ? 0.6 : 1,
    userSelect: 'none',
  }),
  locked: {
    marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
};

export default function PlaylistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { playQueue, currentTrack, toggleShuffle, shuffle } = usePlayerStore();
  const [activeKeys, setActiveKeys] = useState(loadColumns);
  const { data: playlist } = useQuery(['playlist', id], () => getPlaylist(id).then(r => r.data));
  const { data: tracks, isLoading } = useQuery(['playlist-tracks', id], () => getPlaylistTracks(id).then(r => r.data));

  const qc = useQueryClient();

  const deleteMutation = useMutation(
    () => deletePlaylist(id),
    {
      onSuccess: () => {
        qc.invalidateQueries('playlists');
        navigate('/playlists');
      }
    }
  );

  const materializeMutation = useMutation(
    () => materializePlaylist(id),
    {
      onSuccess: () => {
        qc.invalidateQueries(['playlist', id]);
        qc.invalidateQueries(['playlist-tracks', id]);
        qc.invalidateQueries('playlists');
      }
    }
  );

  // Toggle sync_to_aws / is_public. Turning off sync also forces public off,
  // since "public" is only meaningful for a synced playlist.
  const syncMutation = useMutation(
    (patch) => updatePlaylist(id, patch),
    {
      onMutate: async (patch) => {
        await qc.cancelQueries(['playlist', id]);
        const prev = qc.getQueryData(['playlist', id]);
        qc.setQueryData(['playlist', id], (old) => (old ? { ...old, ...patch } : old));
        return { prev };
      },
      onError: (_e, _patch, ctx) => {
        if (ctx?.prev) qc.setQueryData(['playlist', id], ctx.prev);
      },
      onSettled: () => {
        qc.invalidateQueries(['playlist', id]);
        qc.invalidateQueries('playlists');
      },
    }
  );

  const handleToggleSync = () => {
    const next = !playlist?.sync_to_aws;
    // Un-syncing clears public too.
    syncMutation.mutate(next ? { sync_to_aws: true } : { sync_to_aws: false, is_public: false });
  };

  const handleTogglePublic = () => {
    syncMutation.mutate({ is_public: !playlist?.is_public });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${playlist?.name}"? This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  const handleMaterialize = () => {
    if (window.confirm(
      `Convert "${playlist?.name}" to a manual playlist?\n\nThe current ${trackList.length} tracks will be saved as-is. You'll be able to add and remove tracks freely. The smart rules will be removed.`
    )) {
      materializeMutation.mutate();
    }
  };

  const trackList = Array.isArray(tracks) ? tracks : tracks?.results || [];
  const activeColumns = ALL_COLUMNS.filter(c => activeKeys.includes(c.key));

  const { sorted, handleSort, SortIcon } = useSortable(trackList, null);

  const handleShufflePlay = () => {
    const shuffled = shuffleArray(trackList);
    playQueue(shuffled, 0);
  };

return (
  <div>
    <div className="page-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <h1>{playlist?.name || 'Playlist'}</h1>
        <NavControls />
        {playlist?.is_smart && (
          <>
            <button className="btn btn-secondary" onClick={() => navigate(`/playlists/${id}/edit`)}>
              ✨ Edit Rules
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleMaterialize}
              disabled={materializeMutation.isLoading}
              title="Save the current tracks as a regular playlist you can freely edit"
            >
              {materializeMutation.isLoading ? '⏳ Converting…' : '📋 Convert to Manual'}
            </button>
          </>
        )}
        <button
          className="btn btn-secondary"
          onClick={handleDelete}
          style={{ marginLeft: 'auto', color: '#f87171', borderColor: '#f87171' }}
        >
          🗑 Delete Playlist
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 13 }}>
        {playlist?.is_smart ? '✨ Smart Playlist' : '📋 Manual Playlist'} · {trackList.length} tracks
      </p>

      {/* AWS sync controls — manual playlists only (smart sync not supported yet) */}
      {playlist && !playlist.is_smart && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 12 }}>
          <label style={syncLabelStyle(false)} title="Push this playlist (and any songs not already on AWS) to the cloud">
            <input
              type="checkbox"
              checked={!!playlist.sync_to_aws}
              onChange={handleToggleSync}
              disabled={syncMutation.isLoading}
              style={{ marginRight: 8 }}
            />
            ☁️ Sync to AWS
          </label>
          <label style={syncLabelStyle(!playlist.sync_to_aws)} title="When public, any logged-in listener can play it (read-only). When off, only you can see it.">
            <input
              type="checkbox"
              checked={!!playlist.is_public}
              onChange={handleTogglePublic}
              disabled={!playlist.sync_to_aws || syncMutation.isLoading}
              style={{ marginRight: 8 }}
            />
            🌍 Public
          </label>
          {playlist.sync_to_aws && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {playlist.is_public
                ? 'Anyone signed in can listen · read-only to others'
                : 'Private — only you can see it on AWS'}
            </span>
          )}
        </div>
      )}
    </div>

    {isLoading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
      <TrackTable
        tracks={trackList}
        queryKey={['playlist-tracks', id]}
        deletable="playlist"
        playlistId={id}
      />
    )}
  </div>
);

}